import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import scenariosData from './data/scenarios.json';
import highFrequencyData from './data/high-frequency.json';

interface Sentence {
  id: number;
  english: string;
  chinese: string;
}

interface Scenario {
  id: string;
  name: string;
  icon: string;
  description: string;
  sentences: Sentence[];
}

type LearningMode = 'listening' | 'speaking';

const App: React.FC = () => {
  const highFrequencyScenario: Scenario = {
    id: "high_frequency",
    name: "高频语句",
    icon: "⭐",
    description: "1000个最常用的英语句子",
    sentences: highFrequencyData
  };

  const [allScenarios] = useState<Scenario[]>([
    ...scenariosData.scenarios,
    highFrequencyScenario
  ]);

  // 从localStorage获取保存的进度
  const getSavedProgress = () => {
    const savedScenarioId = localStorage.getItem('english-learning-scenario');
    const savedIndex = localStorage.getItem('english-learning-index');
    const savedMode = localStorage.getItem('english-learning-mode');
    
    const scenario = savedScenarioId 
      ? allScenarios.find(s => s.id === savedScenarioId) || allScenarios[0]
      : allScenarios[0];
    const index = savedIndex ? parseInt(savedIndex) : 0;
    const mode = (savedMode as LearningMode) || 'listening';
    
    return { scenario, index, mode };
  };

  const savedProgress = getSavedProgress();
  const [currentScenario, setCurrentScenario] = useState<Scenario>(savedProgress.scenario);
  const [currentIndex, setCurrentIndex] = useState(savedProgress.index);
  const [learningMode, setLearningMode] = useState<LearningMode>(savedProgress.mode);
  const [showTranslation, setShowTranslation] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showScenarioMenu, setShowScenarioMenu] = useState(false);
  const [showModeMenu, setShowModeMenu] = useState(false);
  const [showJumpMenu, setShowJumpMenu] = useState(false);
  const [jumpToIndex, setJumpToIndex] = useState('');
  const [studyTime, setStudyTime] = useState(0); // 学习时长（秒）
  const [availableVoices, setAvailableVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [selectedVoice, setSelectedVoice] = useState<SpeechSynthesisVoice | null>(null);
  const [showVoiceSettings, setShowVoiceSettings] = useState(false);
  const [keepChineseVisible, setKeepChineseVisible] = useState(false); // 听力模式：保持显示中文
  const [keepEnglishVisible, setKeepEnglishVisible] = useState(false); // 口语模式：保持显示英文

  const currentSentence = currentScenario.sentences[currentIndex];
  const totalSentences = currentScenario.sentences.length;

  // 保存学习进度到localStorage
  const saveProgress = (scenarioId: string, index: number, mode: LearningMode) => {
    localStorage.setItem('english-learning-scenario', scenarioId);
    localStorage.setItem('english-learning-index', index.toString());
    localStorage.setItem('english-learning-mode', mode);
  };

  // 格式化学习时间
  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // 播放英语语音
  const playEnglish = useCallback((text: string) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      
      const utterance = new SpeechSynthesisUtterance(text);
      
      // 使用选定的语音引擎
      if (selectedVoice) {
        utterance.voice = selectedVoice;
        utterance.lang = selectedVoice.lang;
      } else {
        utterance.lang = 'en-US';
      }
      
      // 优化语音参数，让声音更自然
      utterance.rate = 0.85;  // 稍微慢一点，更清晰
      utterance.pitch = 1.05; // 稍微高一点，更自然
      utterance.volume = 0.9; // 音量适中
      
      utterance.onstart = () => setIsPlaying(true);
      utterance.onend = () => setIsPlaying(false);
      utterance.onerror = () => setIsPlaying(false);
      
      window.speechSynthesis.speak(utterance);
    } else {
      alert('您的浏览器不支持语音合成功能');
    }
  }, [selectedVoice]);

  // 停止语音播放
  const stopSpeech = () => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      setIsPlaying(false);
    }
  };

  // 上一句
  const previousSentence = useCallback(() => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      // 只有在没有启用"保持显示"时才隐藏翻译
      if (!((learningMode === 'listening' && keepChineseVisible) || 
            (learningMode === 'speaking' && keepEnglishVisible))) {
        setShowTranslation(false);
      }
      stopSpeech();
      saveProgress(currentScenario.id, newIndex, learningMode);
    }
  }, [currentIndex, currentScenario.id, learningMode, keepChineseVisible, keepEnglishVisible]);

  // 下一句
  const nextSentence = useCallback(() => {
    if (currentIndex < totalSentences - 1) {
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      // 只有在没有启用"保持显示"时才隐藏翻译
      if (!((learningMode === 'listening' && keepChineseVisible) || 
            (learningMode === 'speaking' && keepEnglishVisible))) {
        setShowTranslation(false);
      }
      stopSpeech();
      saveProgress(currentScenario.id, newIndex, learningMode);
    }
  }, [currentIndex, totalSentences, currentScenario.id, learningMode, keepChineseVisible, keepEnglishVisible]);

  // 跳转到指定句子
  const jumpToSentence = (index: number) => {
    if (index >= 0 && index < totalSentences) {
      setCurrentIndex(index);
      // 只有在没有启用"保持显示"时才隐藏翻译
      if (!((learningMode === 'listening' && keepChineseVisible) || 
            (learningMode === 'speaking' && keepEnglishVisible))) {
        setShowTranslation(false);
      }
      stopSpeech();
      setShowJumpMenu(false);
      saveProgress(currentScenario.id, index, learningMode);
    }
  };

  // 切换场景
  const switchScenario = (scenario: Scenario) => {
    setCurrentScenario(scenario);
    setCurrentIndex(0);
    // 只有在没有启用"保持显示"时才隐藏翻译
    if (!((learningMode === 'listening' && keepChineseVisible) || 
          (learningMode === 'speaking' && keepEnglishVisible))) {
      setShowTranslation(false);
    }
    stopSpeech();
    setShowScenarioMenu(false);
    saveProgress(scenario.id, 0, learningMode);
  };

  // 切换学习模式
  const switchMode = (mode: LearningMode) => {
    setLearningMode(mode);
    setShowTranslation(false);
    setShowModeMenu(false);
    saveProgress(currentScenario.id, currentIndex, mode);
  };

  // 监听模式和保持显示选项的变化
  useEffect(() => {
    // 如果启用了保持显示，则隐藏手动显示的翻译
    if ((learningMode === 'listening' && keepChineseVisible) || 
        (learningMode === 'speaking' && keepEnglishVisible)) {
      setShowTranslation(false);
    }
  }, [learningMode, keepChineseVisible, keepEnglishVisible]);

  // 处理跳转输入
  const handleJumpSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const index = parseInt(jumpToIndex) - 1;
    if (index >= 0 && index < totalSentences) {
      jumpToSentence(index);
      setJumpToIndex('');
    }
  };

  // 计时器效果
  useEffect(() => {
    const timer = setInterval(() => {
      setStudyTime(prev => prev + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // 键盘快捷键
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        previousSentence();
      } else if (e.key === 'ArrowRight') {
        nextSentence();
      } else if (e.key === ' ') {
        e.preventDefault();
        if (currentSentence) {
          playEnglish(currentSentence.english);
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, [currentIndex, currentSentence, previousSentence, nextSentence, playEnglish]);

  // 语音引擎加载和管理
  useEffect(() => {
    if ('speechSynthesis' in window) {
      const loadVoices = () => {
        const voices = window.speechSynthesis.getVoices();
        const englishVoices = voices.filter(voice => voice.lang.startsWith('en'));
        setAvailableVoices(englishVoices);
        
        // 自动选择最佳语音
        if (englishVoices.length > 0 && !selectedVoice) {
          const preferredVoices = [
            'Google US English',
            'Microsoft Zira - English (United States)',
            'Microsoft Mark - English (United States)',
            'Alex',
            'Samantha',
            'Victoria',
            'Ava',
            'Susan',
            'Karen',
            'Moira',
            'Daniel',
            'Tessa'
          ];
          
          let bestVoice = null;
          
          // 首先尝试找到首选的语音
          for (const preferredName of preferredVoices) {
            bestVoice = englishVoices.find(voice => 
              voice.name.includes(preferredName)
            );
            if (bestVoice) break;
          }
          
          // 如果没有找到首选语音，寻找任何高质量的英语语音
          if (!bestVoice) {
            bestVoice = englishVoices.find(voice => 
              voice.name.includes('Google') || 
              voice.name.includes('Microsoft') || 
              voice.name.includes('Natural') ||
              voice.name.includes('Enhanced')
            );
          }
          
          // 最后选择第一个可用的英语语音
          if (!bestVoice) {
            bestVoice = englishVoices[0];
          }
          
          setSelectedVoice(bestVoice);
        }
      };
      
      // 立即加载语音
      loadVoices();
      
      // 监听语音引擎变化
      window.speechSynthesis.addEventListener('voiceschanged', loadVoices);
      
      return () => {
        window.speechSynthesis.removeEventListener('voiceschanged', loadVoices);
      };
    }
  }, [selectedVoice]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* 顶部导航栏 */}
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3">
          {/* 场景选择和听力/口语模式 */}
          <div className="flex items-center justify-between">
            {/* 场景选择 */}
            <div className="relative">
              <button
                onClick={() => setShowScenarioMenu(!showScenarioMenu)}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
              >
                <span className="text-lg">{currentScenario.icon}</span>
                <span className="text-sm md:text-base font-medium">{currentScenario.name}</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {showScenarioMenu && (
                <div 
                  className="absolute top-full left-0 mt-1 w-60 md:w-72 bg-white rounded-lg shadow-lg border border-gray-200 z-50"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  {allScenarios.map((scenario) => (
                    <button
                      key={scenario.id}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        switchScenario(scenario);
                      }}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center space-x-3 border-none bg-transparent ${
                        currentScenario.id === scenario.id ? 'bg-blue-50' : ''
                      }`}
                    >
                      <span className="text-xl">{scenario.icon}</span>
                      <div>
                        <div className="font-medium text-sm md:text-base">{scenario.name}</div>
                        <div className="text-xs md:text-sm text-gray-500">{scenario.description}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* 听力/口语模式 */}
            <div className="relative">
              <button
                onClick={() => setShowModeMenu(!showModeMenu)}
                className="flex items-center space-x-2 px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
              >
                <span className="text-lg">{learningMode === 'listening' ? '👂' : '🗣️'}</span>
                <span className="text-sm md:text-base font-medium">
                  {learningMode === 'listening' ? '听力模式' : '口语模式'}
                </span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {showModeMenu && (
                <div 
                  className="absolute top-full right-0 mt-1 w-48 md:w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      switchMode('listening');
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center space-x-3 border-none bg-transparent"
                  >
                    <span className="text-lg">👂</span>
                    <span className="text-sm md:text-base">听力模式</span>
                  </button>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      switchMode('speaking');
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center space-x-3 border-none bg-transparent"
                  >
                    <span className="text-lg">🗣️</span>
                    <span className="text-sm md:text-base">口语模式</span>
                  </button>
                  <div className="border-t border-gray-200 p-3">
                    <label 
                      className="flex items-center space-x-2 cursor-pointer"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const newValue = learningMode === 'listening' ? !keepChineseVisible : !keepEnglishVisible;
                        if (learningMode === 'listening') {
                          setKeepChineseVisible(newValue);
                        } else {
                          setKeepEnglishVisible(newValue);
                        }
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={learningMode === 'listening' ? keepChineseVisible : keepEnglishVisible}
                        onChange={() => {}} // 由 label 的 onClick 处理
                        className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                      />
                      <span className="text-sm md:text-base">
                        {learningMode === 'listening' ? '保持显示中文' : '保持显示英文'}
                      </span>
                    </label>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* 主要内容区域 */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-2xl">
          {/* 语音选择 / 计时器 / 跳转句子编号 */}
          <div className="flex items-center justify-between mb-6">
            {/* 语音设置 */}
            <div className="relative">
              <button
                onClick={() => setShowVoiceSettings(!showVoiceSettings)}
                className="flex items-center space-x-2 px-4 py-2 bg-pink-100 text-pink-700 rounded-lg hover:bg-pink-200 transition-colors"
              >
                <span className="text-lg">🎤</span>
                <span className="text-sm md:text-base font-medium">语音</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {showVoiceSettings && (
                <div 
                  className="absolute top-full left-0 mt-1 w-64 md:w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  <div className="p-4" onClick={(e) => e.stopPropagation()}>
                    <h3 className="text-sm md:text-base font-medium text-gray-700 mb-3">选择语音引擎</h3>
                    <div className="space-y-2 max-h-48 md:max-h-60 overflow-y-auto">
                      {availableVoices.map((voice, index) => (
                        <button
                          key={index}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setSelectedVoice(voice);
                            setShowVoiceSettings(false);
                          }}
                          className={`w-full text-left px-3 py-2 rounded-md text-sm md:text-base transition-colors border-none bg-transparent ${
                            selectedVoice?.name === voice.name
                              ? 'bg-pink-100 text-pink-700 border border-pink-200'
                              : 'hover:bg-gray-50'
                          }`}
                        >
                          <div className="font-medium">{voice.name}</div>
                          <div className="text-xs md:text-sm text-gray-500">{voice.lang}</div>
                        </button>
                      ))}
                    </div>
                    {availableVoices.length === 0 && (
                      <div className="text-sm md:text-base text-gray-500 text-center py-4">
                        正在加载语音引擎...
                      </div>
                    )}
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (selectedVoice) {
                            playEnglish("Hello, this is a test.");
                          }
                        }}
                        disabled={!selectedVoice}
                        className="w-full px-3 py-2 bg-pink-600 text-white rounded-md hover:bg-pink-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm md:text-base border-none"
                      >
                        试听语音
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* 计时器 */}
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-orange-100 text-orange-700 rounded-lg">
              <span className="text-lg">⏱️</span>
              <span className="text-sm md:text-base font-mono">{formatTime(studyTime)}</span>
            </div>

            {/* 跳转句子编号 */}
            <div className="relative">
              <button
                onClick={() => setShowJumpMenu(!showJumpMenu)}
                className="flex items-center space-x-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
              >
                <span className="text-sm md:text-base">{currentIndex + 1}/{totalSentences}</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {showJumpMenu && (
                <div className="absolute top-full right-0 mt-1 w-48 md:w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                  <div className="p-4">
                    <form onSubmit={handleJumpSubmit} className="space-y-3">
                      <div>
                        <label className="block text-sm md:text-base font-medium text-gray-700 mb-1">
                          跳转到句子
                        </label>
                        <input
                          type="number"
                          min="1"
                          max={totalSentences}
                          value={jumpToIndex}
                          onChange={(e) => setJumpToIndex(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm md:text-base"
                          placeholder={`1-${totalSentences}`}
                        />
                      </div>
                      <button
                        type="submit"
                        className="w-full px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors text-sm md:text-base border-none"
                      >
                        跳转
                      </button>
                    </form>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* 进度条 */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm md:text-base text-gray-600">{currentScenario.name}</span>
              <span className="text-sm md:text-base text-gray-600">{currentIndex + 1}/{totalSentences}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 md:h-3">
              <div 
                className="bg-blue-600 h-2 md:h-3 rounded-full transition-all duration-300"
                style={{ width: `${((currentIndex + 1) / totalSentences) * 100}%` }}
              />
            </div>
          </div>

          {/* 句子卡片 */}
          {currentSentence && (
            <div className="bg-white rounded-2xl shadow-lg p-6 md:p-10 mb-6 min-h-[300px] md:min-h-[400px] flex flex-col justify-center">
              <div className="text-center space-y-6 md:space-y-8">
                {/* 句子序号 */}
                <div className="inline-flex items-center justify-center w-12 h-12 md:w-16 md:h-16 bg-blue-100 text-blue-600 rounded-full text-lg md:text-xl font-bold">
                  {currentSentence.id}
                </div>

                {/* 主要内容 */}
                <div className="space-y-4 md:space-y-6">
                  {learningMode === 'listening' ? (
                    // 听力模式：显示英文
                    <div className="text-xl md:text-3xl lg:text-4xl font-medium text-gray-900 leading-relaxed">
                      {currentSentence.english}
                    </div>
                  ) : (
                    // 口语模式：显示中文
                    <div className="text-xl md:text-3xl lg:text-4xl font-medium text-gray-900 leading-relaxed">
                      {currentSentence.chinese}
                    </div>
                  )}

                  {/* 翻译内容 - 根据模式和保持显示选项显示 */}
                  {(showTranslation || 
                    (learningMode === 'listening' && keepChineseVisible) || 
                    (learningMode === 'speaking' && keepEnglishVisible)) && (
                    <div className="text-base md:text-xl lg:text-2xl text-gray-600 bg-gray-50 p-4 md:p-6 rounded-lg">
                      {learningMode === 'listening' ? currentSentence.chinese : currentSentence.english}
                    </div>
                  )}
                </div>

                {/* 操作按钮 */}
                <div className="flex flex-col sm:flex-row justify-center items-center space-y-3 sm:space-y-0 sm:space-x-4 md:space-x-6">
                  {/* 播放按钮 */}
                  <button
                    onClick={() => playEnglish(currentSentence.english)}
                    disabled={isPlaying}
                    className={`flex items-center space-x-2 px-6 md:px-8 py-3 md:py-4 rounded-full transition-all text-sm md:text-base ${
                      isPlaying
                        ? 'bg-green-100 text-green-700 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {isPlaying ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 md:h-6 md:w-6 border-b-2 border-green-600"></div>
                        <span>播放中...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5 md:w-6 md:h-6" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                        <span>播放</span>
                      </>
                    )}
                  </button>

                  {/* 显示翻译按钮 - 只有在不是"保持显示"时才显示 */}
                  {!((learningMode === 'listening' && keepChineseVisible) || 
                     (learningMode === 'speaking' && keepEnglishVisible)) && (
                    <button
                      onClick={() => setShowTranslation(!showTranslation)}
                      className={`px-6 md:px-8 py-3 md:py-4 rounded-full transition-all text-sm md:text-base ${
                        showTranslation
                          ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {showTranslation ? 
                        (learningMode === 'listening' ? '隐藏中文' : '隐藏英文') : 
                        (learningMode === 'listening' ? '显示中文' : '显示英文')
                      }
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* 导航按钮 */}
          <div className="flex justify-between items-center">
            <button
              onClick={previousSentence}
              disabled={currentIndex === 0}
              className={`flex items-center space-x-2 px-6 md:px-8 py-3 md:py-4 rounded-full transition-all text-sm md:text-base ${
                currentIndex === 0
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 hover:bg-gray-50 shadow-md'
              }`}
            >
              <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span>上一句</span>
            </button>

            <button
              onClick={nextSentence}
              disabled={currentIndex === totalSentences - 1}
              className={`flex items-center space-x-2 px-6 md:px-8 py-3 md:py-4 rounded-full transition-all text-sm md:text-base ${
                currentIndex === totalSentences - 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 hover:bg-gray-50 shadow-md'
              }`}
            >
              <span>下一句</span>
              <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </main>

      {/* 点击外部区域关闭菜单 */}
      {(showScenarioMenu || showModeMenu || showJumpMenu || showVoiceSettings) && (
        <div 
          className="fixed inset-0 z-40"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setShowScenarioMenu(false);
              setShowModeMenu(false);
              setShowJumpMenu(false);
              setShowVoiceSettings(false);
            }
          }}
        />
      )}
    </div>
  );
};

export default App; 